// SYSTEM_ROUTE: /today
import { useState } from 'react';
import { Button, Chip, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import { type TimelineBlock, type RepairResult, type EntityId, repairDay, admissionDeadlines, fundingFollowUps } from '@life-os/app-core';
import { useServices, useStore } from '../app/store.js';
import { useRouter } from '../app/router.js';
import { useAsync } from '../lib/useAsync.js';
import { seedDemoData } from '../lib/seed.js';
import { Icon } from '../app/icons.js';

function todayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function hhmm(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function Stat({ icon, value, label, onClick }: { icon: string; value: number | string; label: string; onClick?: () => void }) {
  return (
    <div
      className="lc-stat"
      data-clickable={onClick ? 'true' : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
    >
      <span className="lc-stat__icon"><Icon name={icon} size={16} /></span>
      <span className="lc-stat__value">{value}</span>
      <span className="lc-stat__label">{label}</span>
    </div>
  );
}

export function TodayScreen() {
  const services = useServices();
  const { revision, refresh, data } = useStore();
  const { navigate } = useRouter();
  const [quick, setQuick] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [repair, setRepair] = useState<RepairResult | null>(null);

  const date = todayDate();
  const plan = useAsync(() => services.today.buildTodayPlan(date), [revision, date]);
  const admissions = useAsync(() => services.academics.listAdmissions(), [revision]);
  const funding = useAsync(() => services.academics.listFunding(), [revision]);
  const programs = useAsync(() => services.academics.listEducationPrograms(), [revision]);
  const resume = useAsync(() => services.academics.getResumeProfile(), [revision]);

  const replanDay = async () => {
    const [occ, scheduled] = await Promise.all([
      services.events.occurrencesOn(date),
      services.tasks.listByLane('scheduled'),
    ]);
    const blocks: TimelineBlock[] = [
      ...occ.map((o) => ({
        id: `${String(o.eventId)}@${o.startsAt}`,
        title: o.title,
        kind: 'event' as const,
        movable: false,
        startsAt: o.startsAt,
        endsAt: o.endsAt,
      })),
      ...scheduled
        .filter((t) => t.scheduledFor)
        .map((t) => ({
          id: String(t.id),
          title: t.title,
          kind: 'task' as const,
          movable: true,
          startsAt: t.scheduledFor!,
          endsAt: new Date(Date.parse(t.scheduledFor!) + 45 * 60000).toISOString(),
        })),
    ];
    setRepair(repairDay(blocks, new Date().toISOString(), `${date}T23:59:59.999Z`));
  };

  const capture = async () => {
    if (quick.trim().length === 0) return;
    await services.capture.captureItem({ rawText: quick });
    setQuick('');
    refresh();
  };

  const loadSample = async () => {
    setSeeding(true);
    try {
      await seedDemoData(services, data.graph);
      refresh();
    } finally {
      setSeeding(false);
    }
  };

  const now = plan.value?.now ?? [];
  const next = plan.value?.next ?? [];
  const agenda = plan.value?.agenda ?? [];
  const prompts = plan.value?.reviewPrompts ?? [];
  const planEmpty = !plan.loading && now.length === 0 && next.length === 0 && agenda.length === 0 && prompts.length === 0;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const longDate = new Date().toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
  const activeCount = now.length + next.length;

  const focus = now[0] ?? next[0] ?? null;
  const focusLane: 'now' | 'next' | null = now[0] ? 'now' : next[0] ? 'next' : null;

  const nowIso = new Date().toISOString();
  const coming = [
    ...admissionDeadlines(admissions.value ?? [], nowIso).map((d) => ({
      key: `${String(d.admissionId)}-${d.kind}`,
      title: d.label,
      subtitle: `Due ${d.dueDate}`,
      daysLeft: d.daysLeft,
    })),
    ...fundingFollowUps(funding.value ?? [], nowIso).map((f) => ({
      key: `fund-${String(f.fundingId)}`,
      title: `${f.action} · ${f.name}`,
      subtitle: `Next step by ${f.dueDate}`,
      daysLeft: f.daysLeft,
    })),
  ]
    .sort((a, b) => a.daysLeft - b.daysLeft)
    .slice(0, 5);

  const focusAct = async (id: EntityId, complete: boolean, lane?: 'now' | 'waiting') => {
    if (complete) await services.tasks.complete(id);
    else if (lane) await services.tasks.move(id, lane);
    refresh();
  };

  return (
    <>
      <div className="hero">
        <div>
          <h1 className="hero__greeting">{greeting}, Nikhil</h1>
          <div className="hero__sub">
            {longDate} · {activeCount === 0 ? 'a clear slate' : `${activeCount} task${activeCount === 1 ? '' : 's'} in motion`}
            {agenda.length > 0 ? ` · ${agenda.length} on the agenda` : ''}
          </div>
        </div>
      </div>

      {!planEmpty ? (
        <div className="lc-stats">
          <Stat icon="focus" value={now.length} label="In motion" onClick={() => navigate('/tasks')} />
          <Stat icon="tasks" value={next.length} label="Up next" onClick={() => navigate('/tasks')} />
          <Stat icon="calendar" value={agenda.length} label="On agenda" onClick={() => navigate('/calendar')} />
          <Stat icon="review" value={prompts.length} label="To review" onClick={() => navigate('/reviews')} />
        </div>
      ) : null}

      {coming.length > 0 ? (
        <Pane title="Coming up" hint="Hard dates and the next moves the system is watching for you.">
          <Stack>
            {coming.map((d) => (
              <CompactRow
                key={d.key}
                lead={
                  <span style={{ color: d.daysLeft <= 7 ? 'var(--lc-error)' : 'var(--lc-primary)', display: 'grid', placeItems: 'center' }}>
                    <Icon name="calendar" size={18} />
                  </span>
                }
                title={d.title}
                subtitle={d.subtitle}
                onClick={() => navigate('/academics')}
                meta={
                  <Chip tone={d.daysLeft < 0 ? 'danger' : d.daysLeft <= 7 ? 'warn' : 'neutral'}>
                    {d.daysLeft < 0 ? `${Math.abs(d.daysLeft)}d overdue` : d.daysLeft === 0 ? 'today' : `${d.daysLeft}d left`}
                  </Chip>
                }
              />
            ))}
          </Stack>
        </Pane>
      ) : null}

      {(programs.value?.length || resume.value) ? (
        <Pane title="Foundations" hint="Who you are — migrated and synced across your devices.">
          <div className="lc-stats">
            {programs.value?.[0] ? (
              <div className="lc-stat" data-clickable="true" role="button" tabIndex={0} onClick={() => navigate('/academics')} onKeyDown={(e) => { if (e.key === 'Enter') navigate('/academics'); }}>
                <span className="lc-stat__icon"><Icon name="study" size={16} /></span>
                <span className="lc-stat__value">{programs.value[0]!.cgpa ?? '—'}</span>
                <span className="lc-stat__label">{programs.value[0]!.qualification} CGPA</span>
              </div>
            ) : null}
            {programs.value?.[0] ? (
              <div className="lc-stat" data-clickable="true" role="button" tabIndex={0} onClick={() => navigate('/academics')} onKeyDown={(e) => { if (e.key === 'Enter') navigate('/academics'); }}>
                <span className="lc-stat__icon"><Icon name="study" size={16} /></span>
                <span className="lc-stat__value">{programs.value[0]!.courses.length}</span>
                <span className="lc-stat__label">Courses</span>
              </div>
            ) : null}
            {resume.value ? (
              <div className="lc-stat" data-clickable="true" role="button" tabIndex={0} onClick={() => navigate('/career')} onKeyDown={(e) => { if (e.key === 'Enter') navigate('/career'); }}>
                <span className="lc-stat__icon"><Icon name="career" size={16} /></span>
                <span className="lc-stat__value">{resume.value.skills.length}</span>
                <span className="lc-stat__label">Skills</span>
              </div>
            ) : null}
            {resume.value ? (
              <div className="lc-stat" data-clickable="true" role="button" tabIndex={0} onClick={() => navigate('/career')} onKeyDown={(e) => { if (e.key === 'Enter') navigate('/career'); }}>
                <span className="lc-stat__icon"><Icon name="career" size={16} /></span>
                <span className="lc-stat__value">{resume.value.experience.length}</span>
                <span className="lc-stat__label">Roles</span>
              </div>
            ) : null}
          </div>
        </Pane>
      ) : null}

      {focus ? (
        <div className="lc-focus">
          <div className="lc-focus__eyebrow">Primary focus</div>
          <h2 className="lc-focus__title">{focus.title}</h2>
          <div className="lc-focus__action">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <span style={{ color: 'var(--lc-primary)', display: 'grid', placeItems: 'center' }}>
                <Icon name="focus" size={18} />
              </span>
              <span style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>
                {focusLane === 'now' ? 'In progress — finish it before starting anything new.' : 'Ready to start. One thing at a time.'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <Button variant="primary" onClick={() => void focusAct(focus.id, focusLane === 'now', focusLane === 'now' ? undefined : 'now')}>
                {focusLane === 'now' ? 'Mark done' : 'Start now'}
              </Button>
              <Button variant="ghost" onClick={() => void focusAct(focus.id, false, 'waiting')}>
                Defer
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <Pane title="Quick capture" hint="Save a thought now, decide its structure later.">
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: 1 }}
            value={quick}
            placeholder="Capture anything…"
            onChange={(e) => setQuick(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void capture();
            }}
            aria-label="Quick capture"
          />
          <Button variant="primary" onClick={() => void capture()}>
            Capture
          </Button>
        </div>
      </Pane>

      {planEmpty ? (
        <Pane title="First time here?" hint="Optional — populate every system with a realistic example.">
          <Stack>
            <EmptyState title="Your Life OS is empty">
              Load a small sample — goals, targets, habits, a project, calendar events, notes, a health
              check-in, and a finance account — so you can explore how the pieces connect. It is real,
              editable data, not a demo mode.
            </EmptyState>
            <div>
              <Button variant="primary" onClick={() => void loadSample()}>
                {seeding ? 'Loading…' : 'Load sample data'}
              </Button>
            </div>
          </Stack>
        </Pane>
      ) : null}

      <div className="cols-2">
        <Pane title="Now" hint="The few things in motion.">
          <Stack>
            {now.length === 0 ? (
              <EmptyState title="Nothing in Now">Pull a task here when you start it.</EmptyState>
            ) : (
              now.map((t) => <CompactRow key={String(t.id)} title={t.title} subtitle="In progress" />)
            )}
          </Stack>
        </Pane>

        <Pane title="Next" hint="Ready to pick up." action={<Button variant="ghost" onClick={() => navigate('/tasks')}>Open Tasks</Button>}>
          <Stack>
            {next.length === 0 ? (
              <EmptyState title="Next is clear">Promote a captured item into a task.</EmptyState>
            ) : (
              next.slice(0, 6).map((t) => <CompactRow key={String(t.id)} title={t.title} />)
            )}
          </Stack>
        </Pane>
      </div>

      <Pane title="Agenda" hint="Scheduled work and reminders for today.">
        <Stack>
          {agenda.length === 0 ? (
            <EmptyState title="Nothing scheduled">A calm day. Plan one block if you like.</EmptyState>
          ) : (
            agenda.map((a) => (
              <CompactRow
                key={a.id}
                title={a.title}
                subtitle={new Date(a.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              />
            ))
          )}
        </Stack>
      </Pane>

      <Pane title="Replan day" hint="Woke late or drifted? Repack today from now — fixed events stay, the rest flows around them.">
        <Stack>
          <div>
            <Button variant="primary" onClick={() => void replanDay()}>Replan from now</Button>
          </div>
          {repair ? (
            repair.planned.length === 0 ? (
              <EmptyState title="Nothing left to plan">No calendar events or scheduled tasks remain today.</EmptyState>
            ) : (
              <>
                {repair.shifted > 0 ? <Chip tone="warn">{repair.shifted} block{repair.shifted === 1 ? '' : 's'} moved to fit</Chip> : <Chip tone="primary">Day still fits</Chip>}
                {repair.planned.map((b) => (
                  <CompactRow
                    key={b.id}
                    title={b.title}
                    subtitle={`${hhmm(b.startsAt)} – ${hhmm(b.endsAt)}`}
                    meta={<Chip tone={b.movable ? 'primary' : 'neutral'}>{b.movable ? 'movable' : 'fixed'}</Chip>}
                  />
                ))}
                {repair.overflow.length > 0 ? (
                  <div style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>
                    Could not fit today: {repair.overflow.map((b) => b.title).join(', ')}. Defer to tomorrow — no guilt.
                  </div>
                ) : null}
              </>
            )
          ) : null}
        </Stack>
      </Pane>

      {prompts.length > 0 ? (
        <Pane title="Review">
          <Stack>
            {prompts.map((p) => (
              <CompactRow
                key={p.id}
                title={p.label}
                meta={<Button variant="ghost" onClick={() => navigate('/capture')}>Open</Button>}
              />
            ))}
          </Stack>
        </Pane>
      ) : null}
    </>
  );
}
